
import * as React from 'react';
import {
  IComponentDecorator,
  IDecoratedComponentType,
  IDecoratedProps,
  withDisplayName,
  ISimpleDecoratedComponentType,
  ISimpleComponentDecorator,
  ISimpleDecoratedProps
} from './Decorators';

/**
 * @public
 */
export function injectProps<TProps, TOwnProps = {}>(
  getProps: (props: TOwnProps) => (((props: TOwnProps) => TProps) | TProps)
): IComponentDecorator<TProps, TOwnProps> {
  return <P extends TProps>(component: React.ComponentType<P>): IDecoratedComponentType<P, TProps, TOwnProps> => {
    const Component = component;

    let memoizedGetProps: (props: TOwnProps) => TProps;

    type IProps = IDecoratedProps<P, TProps, TOwnProps>;

    class Injector extends React.PureComponent<IProps, TProps> {
      constructor(props: IProps) {
        super(props);

        const getPropsResult = getProps(this.props as Readonly<TOwnProps>);

        if (isMemoizedResult(getPropsResult)) {
          memoizedGetProps = getPropsResult;
          this.state = getPropsResult(this.props as Readonly<TOwnProps>);
        } else {
          memoizedGetProps = getProps as (props: TOwnProps) => TProps;
          this.state = getPropsResult;
        }
      }

      public componentWillReceiveProps(nextProps: IProps): void {
        this.setState(memoizedGetProps(nextProps as Readonly<TOwnProps>));
      }

      public render(): JSX.Element {
        return (
          <Component
            { ...this.props }
            { ...this.state }
          />
        );
      }
    }

    return withDisplayName('InjectProps')(Injector);
  };
}

/**
 * @public
 */
export function injectInitialProps<TProps, TOwnProps = {}>(
  getInitialProps: (props: TOwnProps) => TProps
): IComponentDecorator<TProps, TOwnProps> {
  return <P extends TProps>(component: React.ComponentType<P>): IDecoratedComponentType<P, TProps, TOwnProps> => {
    const Component = component;

    interface IInitializerState {
      initialProps: TProps;
    }

    type IProps = IDecoratedProps<P, TProps, TOwnProps>;

    class Initializer extends React.PureComponent<IProps, IInitializerState> {
      constructor(props: IProps) {
        super(props);

        this.state = {
          initialProps: getInitialProps(this.props as IProps)
        };
      }

      public render(): JSX.Element {
        return (
          <Component
            { ...this.state.initialProps }
            { ...this.props }
          />
        );
      }
    }

    return withDisplayName('InjectInitialProps')(Initializer);
  };
}

/**
 * @public
 */
export function wrapWithParent<TOwnProps>(
  parent: React.ComponentType<TOwnProps>
): ISimpleComponentDecorator<TOwnProps> {
  const Parent = parent;

  return <P extends {}>(
    component: React.ComponentType<P>
  ): ISimpleDecoratedComponentType<P, TOwnProps> => {
    const Component = component;

    return (props: ISimpleDecoratedProps<P, TOwnProps> & Pick<React.Props<{}>, 'children'>): JSX.Element => {
      const {
        children,
        ...ownProps
      } = props as Pick<React.Props<{}>, 'children'>;

      return (
        <Parent { ...ownProps }>
          <Component { ...ownProps }>{ children }</Component>
        </Parent>
      );
    };
  };
}

function isMemoizedResult<TProps, TOwnProps>(
  getPropsResult: ((props: TOwnProps) => TProps) | TProps
): getPropsResult is ((props: TOwnProps) => TProps) {
  return typeof getPropsResult === 'function';
}
